import { useEffect } from 'react';
import { isIdentificationAllowed } from '../services/identificationConsent';
import { logger } from '../utils/core/logger';

/**
 * Type declaration for the Snitcher Radar global object
 */
interface SnitcherRadarInstance {
    initialized?: boolean;
    _loaded?: boolean;
    push: (args: unknown[]) => void;
    bootstrap: () => void;
    flush?: () => Promise<void>;
    sessionManager?: {
        currentSession?: {
            id?: string;
        };
    };
    [key: string]: unknown;
}

type SnitcherRadarMethod = (...funcArgs: unknown[]) => unknown;
type SnitcherRadarWithMethods = SnitcherRadarInstance & Record<string, SnitcherRadarMethod>;
type SnitcherImportMetaEnv = ImportMetaEnv & {
    readonly VITE_SNITCHER_PROFILE_ID?: string;
};

declare global {
    interface Window {
        RosRd?: SnitcherRadarInstance;
    }
}

/**
 * Module-level storage for the Snitcher session ID.
 * Captured from sessionManager.currentSession.id after SDK loads.
 */
let snitcherSessionId: string | null = null;

const MAX_INITIALIZATION_ATTEMPTS = 3;
const SCRIPT_LOAD_TIMEOUT_MS = 5000;
const RETRY_BASE_DELAY_MS = 500;
let loaderState: 'idle' | 'loading' | 'loaded' | 'initialized' | 'failed' = 'idle';
let initialization: Promise<void> | null = null;

/**
 * Get the session ID from the SDK's sessionManager.
 */
function getSessionIdFromSDK(radar: SnitcherRadarInstance): string | null {
    const sessionId = radar.sessionManager?.currentSession?.id;
    if (sessionId && typeof sessionId === 'string') {
        return sessionId;
    }
    return null;
}

/**
 * Get the captured Snitcher session ID.
 */
export function getSnitcherSessionId(): string | null {
    // Return cached value if available
    if (snitcherSessionId) {
        return snitcherSessionId;
    }

    // Try to get from SDK if loaded
    if (typeof window !== 'undefined' && window.RosRd?.initialized) {
        const sdkId = getSessionIdFromSDK(window.RosRd);
        if (sdkId) {
            snitcherSessionId = sdkId;
            return snitcherSessionId;
        }
    }

    return null;
}

/**
 * Flush any queued Snitcher events to ensure data is synced.
 * Call this before making backend API calls that need the session data.
 */
export async function flushSnitcherData(): Promise<void> {
    // Consent withdrawn after Radar loaded: stop sending it data (IX-5465).
    if (!isIdentificationAllowed()) return;
    if (typeof window !== 'undefined' && window.RosRd?.flush) {
        try {
            await window.RosRd.flush();
            logger.debug('[Snitcher Radar] Data flushed successfully');
        } catch (error) {
            logger.debug('[Snitcher Radar] Flush failed:', error);
        }
    }
}

/**
 * Wait for Snitcher Radar to be initialized and session ID available.
 *
 * This function polls for the Snitcher session ID until it's available
 * or the timeout is reached. Use this before making API calls that
 * require the Snitcher session ID for enrichment.
 *
 * @param timeoutMs - Maximum time to wait in milliseconds (default: 3000ms)
 * @returns The Snitcher session ID if available, null if timeout reached or not configured
 */
export async function waitForSnitcherReady(timeoutMs: number = 3000): Promise<string | null> {
    const pollInterval = 100;
    const maxAttempts = Math.ceil(timeoutMs / pollInterval);

    for (let i = 0; i < maxAttempts; i++) {
        const sessionId = getSnitcherSessionId();
        if (sessionId) {
            logger.debug('[Snitcher Radar] Ready with session ID after', i * pollInterval, 'ms');
            return sessionId;
        }
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    logger.debug('[Snitcher Radar] Timeout waiting for session ID after', timeoutMs, 'ms');
    return null;
}

export async function ensureSnitcherRadarReady(
    apiUrl?: string,
    timeoutMs: number = 3000
): Promise<string | null> {
    const didStart = initializeSnitcherRadar(apiUrl);
    if (!didStart) {
        return null;
    }

    return waitForSnitcherReady(timeoutMs);
}

/**
 * Get the Snitcher Profile ID from environment variables
 */
function getSnitcherProfileId(): string | null {
    // Try Vite DefinePlugin globals first (used by widget)
    if (typeof window !== 'undefined') {
        const definePluginId =
            typeof __INBOUNDX_SNITCHER_PROFILE_ID__ !== 'undefined'
                ? __INBOUNDX_SNITCHER_PROFILE_ID__
                : null;
        if (definePluginId) {
            return definePluginId;
        }
    }

    // Try Vite environment variables (used by preprod, chrome-plugin)
    if (typeof import.meta !== 'undefined' && import.meta.env) {
        const viteEnv = import.meta.env as SnitcherImportMetaEnv;
        return viteEnv.VITE_SNITCHER_PROFILE_ID || null;
    }

    return null;
}

/**
 * Hook to load Snitcher Radar tracking script when ExpandedChatView is displayed
 *
 * This hook injects the Snitcher Radar script dynamically and initializes it
 * with the RosRd namespace. Callers share initialization with bounded retries,
 * and the SDK remains loaded even when the component unmounts.
 *
 * Configuration:
 * - CDN: cdn.snitcher.com
 * - API: radar.snitcher.com
 * - Namespace: RosRd
 * - waitForConsent: false
 * - All tracking features disabled (we only need session UUID for enrichment)
 */
interface SnitcherRadarOptions {
    apiUrl?: string;
    /**
     * Gate the (paid) Radar script load. When false the hook is a no-op, so we
     * don't pay Snitcher for anonymous bounce traffic. Callers pass true only
     * when "identify all visitors" is on or the visitor has engaged. Flipping
     * false -> true at runtime re-runs the effect and bootstraps the SDK then.
     * Defaults to true for backward compatibility.
     */
    enabled?: boolean;
}

function getSnitcherProxyHost(apiUrl?: string): string | null {
    if (!apiUrl) {
        return null;
    }

    try {
        const parsedUrl = new URL(apiUrl);
        if (parsedUrl.hostname.endsWith('userose.ai')) {
            return parsedUrl.hostname;
        }
    } catch (error) {
        logger.debug('[Snitcher Radar] Failed to parse apiUrl for proxy host:', error);
    }

    return null;
}

function getSnitcherEndpoints(apiUrl?: string): { cdn: string; apiEndpoint: string } {
    const proxyHost = getSnitcherProxyHost(apiUrl);
    if (proxyHost) {
        return {
            cdn: `${proxyHost}/account-context/sdk`,
            apiEndpoint: `${proxyHost}/account-context/api`,
        };
    }

    return {
        cdn: 'cdn.snitcher.com',
        apiEndpoint: 'radar.snitcher.com',
    };
}

function initializeSnitcherRadar(apiUrl?: string): boolean {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false;
    }

    // The SDK queue's _loaded flag only indicates injection, not readiness.
    if (window.RosRd?.initialized) {
        loaderState = 'initialized';
        return true;
    }

    // All callers share the same operation, including its backoff periods.
    if (initialization) return true;
    // A loaded script may still be initializing. Removing it cannot unload its
    // code, so readiness callers must wait for that SDK rather than inject again.
    if (loaderState === 'loaded') return true;
    // The retry budget belongs to the tab, not to individual callers.
    if (loaderState === 'failed') return false;

    // Get profile ID from environment
    const profileId = getSnitcherProfileId();
    if (!profileId) {
        logger.debug('[Snitcher Radar] No profile ID configured, skipping initialization');
        return false;
    }

    initialization = initializeWithRetries(profileId, getSnitcherEndpoints(apiUrl)).finally(() => {
        initialization = null;
    });
    return true;
}

async function initializeWithRetries(
    profileId: string,
    endpoints: ReturnType<typeof getSnitcherEndpoints>
): Promise<void> {
    for (let attempt = 1; attempt <= MAX_INITIALIZATION_ATTEMPTS; attempt++) {
        // An SDK may finish initializing during the backoff period.
        if (window.RosRd?.initialized) {
            loaderState = 'initialized';
            return;
        }
        loaderState = 'loading';
        if (await loadSnitcherRadar(profileId, endpoints, attempt)) {
            loaderState = window.RosRd?.initialized ? 'initialized' : 'loaded';
            return;
        }
        loaderState = 'failed';
        if (attempt < MAX_INITIALIZATION_ATTEMPTS) {
            const delay =
                RETRY_BASE_DELAY_MS * 2 ** (attempt - 1) + Math.random() * RETRY_BASE_DELAY_MS;
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }
    logger.warn('[Snitcher Radar] Script load retries exhausted', {
        maxAttempts: MAX_INITIALIZATION_ATTEMPTS,
    });
}

function loadSnitcherRadar(
    profileId: string,
    { cdn, apiEndpoint }: ReturnType<typeof getSnitcherEndpoints>,
    attempt: number
): Promise<boolean> {
    // Initialize the Snitcher Radar script
    const config = {
        cdn,
        apiEndpoint,
        profileId: profileId,
        namespace: 'RosRd',
        waitForConsent: false,
        // Disable all tracking features - we only need session UUID for enrichment
        formTracking: false,
        clickTracking: false,
        downloadTracking: false,
        errorCapture: false,
    };

    // Replace a failed queue so stale calls cannot leak into the next attempt.
    const radar = [] as unknown as SnitcherRadarInstance;
    window.RosRd = radar;
    // Retained for SDK bootstrap compatibility; never used as our success flag.
    radar._loaded = true;

    // Define the API methods that queue calls until the SDK is loaded
    const methods = [
        'track',
        'page',
        'identify',
        'group',
        'alias',
        'ready',
        'debug',
        'on',
        'off',
        'once',
        'trackClick',
        'trackSubmit',
        'trackLink',
        'trackForm',
        'pageview',
        'screen',
        'reset',
        'register',
        'setAnonymousId',
        'addSourceMiddleware',
        'addIntegrationMiddleware',
        'addDestinationMiddleware',
        'flush',
    ];

    methods.forEach((method) => {
        (radar as SnitcherRadarWithMethods)[method] = (...funcArgs: unknown[]) => {
            const r = window.RosRd;
            if (!r) return;
            if (r.initialized) {
                return (r as SnitcherRadarWithMethods)[method].apply(r, funcArgs);
            }
            const args: unknown[] = [method, ...funcArgs];
            r.push(args);
            return r;
        };
    });

    return new Promise((resolve) => {
        document.getElementById('__radar__')?.remove();
        const script = document.createElement('script');
        script.async = true;
        script.type = 'text/javascript';
        script.id = '__radar__';
        script.dataset.settings = JSON.stringify(config);
        let settled = false;
        let bootstrapped = false;

        const finish = (reason?: 'script_error' | 'timeout' | 'injection_error') => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            script.onload = null;
            script.onerror = null;
            if (reason) {
                radar._loaded = false;
                script.remove();
                if (!window.RosRd?.initialized) delete window.RosRd;
                logger.debug('[Snitcher Radar] Initialization attempt failed', {
                    reason,
                    attempt,
                    maxAttempts: MAX_INITIALIZATION_ATTEMPTS,
                });
            } else {
                snitcherSessionId = window.RosRd?.initialized
                    ? getSessionIdFromSDK(window.RosRd)
                    : null;
                logger.debug('[Snitcher Radar] Script loaded');
            }
            resolve(!reason);
        };
        const timeout = setTimeout(() => finish('timeout'), SCRIPT_LOAD_TIMEOUT_MS);

        // End the retry loop on load, independently of async SDK initialization.
        // waitForSnitcherReady retains each caller's bounded readiness polling.
        script.onload = () => finish();
        script.onerror = () => finish('script_error');

        radar.bootstrap = () => {
            if (bootstrapped || settled) return;
            bootstrapped = true;
            try {
                script.src = `https://${config.cdn}/releases/latest/radar.min.js`;
                const firstScript = document.scripts[0];
                if (firstScript?.parentNode) {
                    firstScript.parentNode.insertBefore(script, firstScript);
                } else {
                    document.head.appendChild(script);
                }
            } catch {
                finish('injection_error');
            }
        };

        radar.bootstrap();
    });
}

export function useSnitcherRadar({ apiUrl, enabled = true }: SnitcherRadarOptions = {}): void {
    useEffect(() => {
        if (!enabled) {
            logger.debug('[Snitcher Radar] Disabled, skipping initialization');
            return;
        }

        initializeSnitcherRadar(apiUrl);

        // No cleanup needed - script remains loaded once initialized
    }, [apiUrl, enabled]);
}
